import type { AuthedRequester } from "@/lib/brokerAuth";

// The broker's in-app inbox seam — `/api/broker/notifications/**` (PMS-S353,
// pairs BRK-S157). Types mirror the PMS contract (docs/openapi.yaml). The inbox
// is the broker's `notification` rows where channel='in_app'; merely LISTING them
// marks the returned rows SEEN server-side, so the bell badge — the UNSEEN count —
// clears when the /notifications page opens. Timestamps are ISO strings on the
// wire (the PMS Date columns serialize to strings over JSON).

const BASE = "/api/broker/notifications";

/** One in-app notification (PMS InboxItem). */
export interface InboxItem {
  id: string;
  communicationId: string;
  communicationNumber: string;
  noticeType: string | null;
  purpose: string;
  subject: string;
  body: string | null;
  priorityTier: "critical" | "transactional" | "promotional";
  createdAt: string;
  seenAt: string | null;
  readAt: string | null;
  /**
   * WHICH record this notice is about (BRK-S177, consuming PMS-S466).
   *
   * ⚠️ READ AHEAD OF THE PIN. The inbox item publishes `noticeType` today and
   * nothing else about its subject, so a `bid_result_needed` row can say what
   * KIND of notice it is and not which order it chases — which is the one thing
   * the in-app half of that reminder needs in order to carry the same link the
   * EMAIL already does (PMS's `bidResultUrl`). The `communication` row already
   * holds the nullable typed FKs and the `subject_kind` discriminator
   * (PMS-S345), so PMS-S466 is a read-model widening rather than a schema
   * change.
   *
   * OPTIONAL and NULLABLE, which renders no link at all until it arrives, and
   * `test/lib/brokerNotificationsContract.test.ts` fails the day the shape is
   * pinned — so this note cannot outlive its own truth. `null` is a real value
   * too: an internal or account-wide notice is about no order.
   *
   * The POINTER id, never a discovery id — it is what
   * `/surety/submissions/{id}` takes.
   */
  bondSubmissionId?: string | null;
}

/**
 * The notice types this build knows how to send a broker somewhere for, and
 * where on the order each one lands. Open upstream (PMS adds events on its own
 * cadence), so this is a lookup and never a union `noticeType` is narrowed to:
 * an unrecognised notice still lists, it just carries no link.
 *
 * `gia_needed` (BRK-S182, the PMS-S486 reminder) joined `bid_result_needed`:
 * both deep-link to the submission profile with the `?focus=` PMS's email
 * template builds, so both halves of one reminder land in the same place.
 */
const NOTICE_DESTINATIONS: Readonly<
  Record<string, { focus: string; label: string }>
> = {
  bid_result_needed: {
    focus: "bid-result",
    label: "Record your bid result →",
  },
  gia_needed: { focus: "gia", label: "Send the GIA for signature →" },
};

/** Where an inbox row sends the broker, and what the link says. */
export interface NotificationDestination {
  to: string;
  label: string;
}

/**
 * Where an inbox row sends the broker, or **null** when there is nowhere
 * useful (BRK-S177).
 *
 * Pure, so the rule is testable without a render, and ONE place so the inbox
 * and any later surface cannot disagree about where a notice points.
 *
 * It needs BOTH halves — the notice type and the record's id — and that is the
 * point rather than a limitation: a row that says it is a bid-result chaser but
 * not which order it chases has nowhere to go, and inventing a destination
 * (the list, the account) would answer a different question than the one the
 * broker clicked. Until PMS-S466 publishes `bondSubmissionId`, that is every
 * live row — and no workaround is built for it.
 */
export function notificationDestination(
  item: Pick<InboxItem, "noticeType" | "bondSubmissionId">,
): NotificationDestination | null {
  const known =
    item.noticeType === null ? undefined : NOTICE_DESTINATIONS[item.noticeType];
  const id = item.bondSubmissionId;
  if (!known || !id) return null;
  return {
    to: `/surety/submissions/${encodeURIComponent(id)}?focus=${known.focus}`,
    label: known.label,
  };
}

/** The destination's URL alone — see `notificationDestination`. */
export function notificationLink(
  item: Pick<InboxItem, "noticeType" | "bondSubmissionId">,
): string | null {
  return notificationDestination(item)?.to ?? null;
}

/** The paged inbox envelope (the shared PMS list shape). */
export interface InboxPage {
  items: InboxItem[];
  total: number;
  limit: number;
  offset: number;
}

/** The NARROW engagement state read/archive return — never the delivery columns. */
export interface InboxItemState {
  id: string;
  seenAt: string | null;
  readAt: string | null;
  archivedAt: string | null;
}

export interface InboxQuery {
  limit?: number;
  offset?: number;
}

/** GET /api/broker/notifications — the broker's own inbox, newest first (paged).
 *  Rendering the returned rows marks them SEEN server-side (the badge clears). */
export function listNotifications(
  request: AuthedRequester,
  query: InboxQuery = {},
): Promise<InboxPage> {
  const qs = new URLSearchParams();
  if (query.limit != null) qs.set("limit", String(query.limit));
  if (query.offset != null) qs.set("offset", String(query.offset));
  const s = qs.toString();
  return request<InboxPage>(`${BASE}${s ? `?${s}` : ""}`);
}

/** GET /api/broker/notifications/unread-count — the UNSEEN bell-badge count.
 *  (The route keeps the S353 name; the value is the unseen count.) */
export function getUnseenCount(request: AuthedRequester): Promise<number> {
  return request<{ count: number }>(`${BASE}/unread-count`).then(
    (r) => r.count,
  );
}

/** POST /api/broker/notifications/{id}/read — open one item (clears its bold). */
export function markNotificationRead(
  request: AuthedRequester,
  id: string,
): Promise<InboxItemState> {
  return request<InboxItemState>(`${BASE}/${id}/read`, { method: "POST" });
}

/** POST /api/broker/notifications/{id}/archive — dismiss one item from the feed. */
export function archiveNotification(
  request: AuthedRequester,
  id: string,
): Promise<InboxItemState> {
  return request<InboxItemState>(`${BASE}/${id}/archive`, { method: "POST" });
}

/** Unread = delivered but not opened yet (readAt null) — the list's bold rows. */
export function isUnread(item: Pick<InboxItem, "readAt">): boolean {
  return item.readAt === null;
}
